import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '@/app/providers/user';
import { parseAuthCallback } from '@/features/auth/services/authCallback';
import { takeReturnTo } from '@/features/auth/services/returnTo';
import { logger } from '@/shared/utils/logger';
import { Button, Spinner, ValoiVLockup } from '@/shared/ui';

// The `error` query/fragment param is attacker-influenceable (anyone can craft
// a /auth/callback URL), so it is mapped to copy here — never rendered verbatim.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Sign-in was canceled. Please try again.',
};

// Landing route for the Google redirect (Option A): the backend has already
// exchanged the code and set the refresh cookie, then bounced the browser here
// with the access token in the URL fragment (or query), e.g.
//   /auth/callback#access_token=...&expires_in=900
export default function AuthCallbackPage() {
  const { completeGoogleLogin } = useCurrentUser();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handled = useRef(false);

  useEffect(() => {
    // Guard against React StrictMode's double-invoke (and the token being
    // stripped from the URL on the second pass).
    if (handled.current) return;
    handled.current = true;

    const { accessToken, error: authError } = parseAuthCallback({
      hash: window.location.hash,
      search: window.location.search,
    });

    // Strip the token from the URL so it doesn't linger in history.
    window.history.replaceState(null, '', window.location.pathname);

    if (authError || !accessToken) {
      if (authError) logger.error('OAuth callback returned error:', authError);
      setError(
        (authError && AUTH_ERROR_MESSAGES[authError]) ||
          'Sign-in did not complete. Please try again.',
      );
      return;
    }

    completeGoogleLogin(accessToken)
      .then(() => navigate(takeReturnTo(), { replace: true }))
      .catch(() => setError('Could not load your profile. Please try signing in again.'));
  }, [completeGoogleLogin, navigate]);

  if (error) {
    return (
      <div className="auth">
        <div className="auth__card">
          <ValoiVLockup title="Valoi" className="auth__logo" />
          <p role="alert" className="auth__error">
            {error}
          </p>
          <Button variant="primary" block onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth">
      <div className="auth__card auth__card--quiet">
        <Spinner size={26} label="Signing you in" />
        <p className="auth__lead">Signing you in…</p>
      </div>
    </div>
  );
}
